import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation, useQuery } from '@src/shared/query';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { usersUrl } from './constants';
import {
  type BranchSearchResponse,
  type BranchSearchResponseDto,
  type DepartmentResponse,
  type EmploymentResponse,
  type EmploymentResponseDto,
  type JobTitleResponse,
  type ManagerSearchResponse,
  type RankResponse,
  type StatusChoicesResponse,
  type TeamResponse,
  type TitleResponse,
  type UserList,
  type UserUpdateDto,
  type UsersResponse,
  type UserSearchResponse,
  type UseUserSearchParams,
  type UseUsersParams, type RankResponseDto, type TitleResponseDto,
  jobTitleFromDto, titleFromDto, type JobTitleResponseDto,
  type TeamResponseDto, teamFromDto, type DepartmentResponseDto,
  type UserDetailDto, type UserDetail,
} from "@src/features/users";
import {
  userDetailFromDto,
  userListFromDto
} from '@src/features/users/lib/user';
import {branchSearchFromDto} from "@src/features/users/lib/branch.ts";
import {rankFromDto} from "@src/features/users/lib/rank.ts";
import {departmentFromDto} from "@src/features/users/lib/department.ts";
import {employmentFromDto} from "@src/features/users/lib/employment.ts";
import { serialize } from '@src/shared/lib/formUtils';
import type { UseQueryParams } from "@src/features/types";

export type CreateUserPayload = UserUpdateDto;
export type CreateUserResponse = Record<string, unknown> & {
  id?: number | string;
  user_id?: number | string;
};
export type UpdateUserPayload = {
  id: number | string;
  payload: UserUpdateDto;
};
export type UpdateUserResponse = Record<string, unknown>;
export type UsersListResponse = Omit<UsersResponse, 'results'> & {
  results: UserList[];
};

export function useUsers({ pageSize, ordering, filterParams }: UseUsersParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: ['root', 'users', 'admin-grid', pageSize, ordering, filterParams],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: usersUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<UsersResponse>,
    select: (data): InfiniteData<UsersListResponse> => ({
      ...data,
      pages: (data.pages ?? []).map((page) => ({
        ...page,
        results: page.results.map(userListFromDto),
      })),
    }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
  });
}



export function useCreateUser() {
  return useMutation<CreateUserResponse, Error, CreateUserPayload>({
    mutationKey: ['root', 'users', 'create'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: usersUrl,
        method: 'POST',
        payload: serialize(payload),
        contentType: 'multipart/form-data',
      }) as Promise<CreateUserResponse>,
  });
}

export function useUpdateUser() {
  return useMutation<UpdateUserResponse, Error, UpdateUserPayload>({
    mutationKey: ['root', 'users', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `${usersUrl}${id}/`,
        method: 'PATCH',
        payload: serialize(payload),
        contentType: 'multipart/form-data',
      }) as Promise<UpdateUserResponse>,
  });
}

export function useUserById(id: number | string | null | undefined, enabled = true) {
  return useQuery<UserDetailDto, UserDetail>(`users/${String(id ?? '')}/`, {
    enabled: Boolean(id) && enabled,
    select: (data): UserDetail => userDetailFromDto(data),
  });
}

export function useUserStatusChoices() {
  return useQuery<StatusChoicesResponse>('users/choices/user-statuses/', {
    initialData: {},
  });
}


export function useUserBranchChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<BranchSearchResponseDto, BranchSearchResponse>('users/branch/search/', {
    queryParams,
    enabled,
    select: (data): BranchSearchResponse => ({
      ...data,
      results: data.results.map(branchSearchFromDto),
    }),
  });
}

export function useUserManagerChoices(params: Partial<UseUserSearchParams> = {}) {
  const { query = '', enabled = true } = params;

  return useQuery<ManagerSearchResponse>('users/managers/', {
    queryParams: {
      q: query,
    },
    enabled,
  });
}

export function useUserSearchChoices(params: Partial<UseUserSearchParams> = {}) {
  const { query = '', enabled = true } = params;

  return useQuery<UserSearchResponse>('users/search/', {
    queryParams: {
      q: query,
    },
    enabled,
    initialData: { results: [] },
  });
}

export function useUserRankChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<RankResponseDto, RankResponse>('users/rank/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): RankResponse => ({
      ...data,
      results: data.results.map(rankFromDto),
    }),
  });
}

export function useUserTitleChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<TitleResponseDto, TitleResponse>('users/title/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): TitleResponse => ({
      ...data,
      results: data.results.map(titleFromDto),
    }),
  });
}

export function useUserJobTitleChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<JobTitleResponseDto, JobTitleResponse>('users/job-title/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): JobTitleResponse => ({
      ...data,
      results: data.results.map(jobTitleFromDto),
    }),
  });
}

export function useUserTeamChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<TeamResponseDto, TeamResponse>('users/team/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): TeamResponse => ({
      ...data,
      results: data.results.map(teamFromDto),
    }),
  });
}

export function useUserDepartmentChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<DepartmentResponseDto, DepartmentResponse>('users/department/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): DepartmentResponse => ({
      ...data,
      results: data.results.map(departmentFromDto),
    }),
  });
}

export function useUserEmploymentChoices(params: Partial<UseQueryParams> = {}) {
  const { queryParams = {}, enabled = true } = params;

  return useQuery<EmploymentResponseDto, EmploymentResponse>('users/employment/', {
    queryParams,
    enabled,
    initialData: { results: [] },
    select: (data): EmploymentResponse => ({
      ...data,
      results: data.results.map(employmentFromDto),
    }),
  });
}
